import React, { useCallback } from 'react';

import PropTypes from 'prop-types';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useLocation } from 'react-router-dom';
import cn from 'classnames';
import { Spinner } from '@picsio/ui';

import { getActiveProduct } from '../../../store/products/selectors';
import {
  selectEffectiveChannelState,
  selectEffectiveIsMasterView,
  selectListingsLiveRefreshing,
} from '../../../store/selectors/productSelectors';
import { selectDraftScopeChrome } from '../../../store/selectors/draftScopeChromeSelectors';
import l10n from '../../../shared/strings';
import Logger from '../../../services/Logger';
import DraftScopeDetailsPlaceholder from '../../DraftScopeDetailsPlaceholder';
import startEditDraftTextFlow from '../../HowDoYouWantToEditTextDialog/startEditDraftTextFlow';
import { productStateTabConstants } from '../constants/productStateTabConstants';
import StagingTab from '../StagingTab';
import LiveTab from '../LiveTab';
import MasterTab from '../MasterTab';
import ProductStateTabsBar from './ProductStateTabsBar';
import styles from './styles.module.scss';

const ProductStateTabs = ({ productId, variantId: variantIdProp }) => {
  const dispatch = useDispatch();
  const store = useStore();
  const location = useLocation();
  const variantId = variantIdProp ?? getActiveProduct(location)?.variantId;
  const { activeTab, hasLive } = useSelector((state) => selectEffectiveChannelState(state, productId, variantId));
  const isMasterView = useSelector((state) => selectEffectiveIsMasterView(state, productId, variantId));
  const isLiveRefreshing = useSelector((state) => selectListingsLiveRefreshing(state, productId));
  const showLiveSyncOverlay = !isMasterView
    && activeTab === productStateTabConstants.PUBLISHED
    && isLiveRefreshing;
  const draftScopeChrome = useSelector((state) => (
    selectDraftScopeChrome(state, productId, variantId)
  ));
  const handleEditText = useCallback(async () => {
    try {
      await startEditDraftTextFlow({
        dispatch,
        getState: store.getState,
        productId,
        variantId,
      });
    } catch (error) {
      Logger.error(error, { error, productId }, ['EditDraftTextFailed', { productId }]);
    }
  }, [dispatch, productId, store.getState, variantId]);

  return (
    <>
      <ProductStateTabsBar
        productId={productId}
        variantId={variantId}
        onEditText={handleEditText}
      />
      <div
        className={cn('InfoPanelBody', styles.content, {
          [styles.contentCentered]: !isMasterView
            && activeTab === productStateTabConstants.DRAFT
            && draftScopeChrome?.hideDetails,
        })}
      >
        <If condition={showLiveSyncOverlay}>
          <div className={styles.syncingOverlay}>
            <Spinner text={l10n.PRODUCT_INFO.syncingFromChannel} />
          </div>
        </If>
        <Choose>
          <When condition={isMasterView}>
            <MasterTab productId={productId} variantId={variantId} />
          </When>
          <Otherwise>
            <Choose>
              <When condition={activeTab === productStateTabConstants.DRAFT && draftScopeChrome?.hideDetails}>
                <DraftScopeDetailsPlaceholder hasLive={hasLive} onEditText={handleEditText} />
              </When>
              <When condition={activeTab === productStateTabConstants.DRAFT}>
                <StagingTab productId={productId} variantId={variantId} />
              </When>
              <When condition={activeTab === productStateTabConstants.PUBLISHED}>
                <LiveTab productId={productId} variantId={variantId} />
              </When>
            </Choose>
          </Otherwise>
        </Choose>
      </div>
    </>
  );
};

ProductStateTabs.propTypes = {
  productId: PropTypes.string.isRequired,
  variantId: PropTypes.string,
};

ProductStateTabs.defaultProps = {
  variantId: null,
};

export default ProductStateTabs;
